import { PoseWorkerClient } from '../PoseWorkerClient';
import type { DetectionBox } from './Detector';
import type { DeepReIdExtractorLike, DeepReIdOptions } from './DeepReIdExtractor';
import type { DeepFeature } from '../tracking/ReIdFeatures';

export class DeepReIdExtractorWorker implements DeepReIdExtractorLike {
  private readonly client: PoseWorkerClient;
  private initialized = false;
  private readonly ownsClient: boolean;

  constructor(
    private readonly opts: DeepReIdOptions,
    sharedClient?: PoseWorkerClient,
  ) {
    this.client = sharedClient ?? new PoseWorkerClient();
    this.ownsClient = !sharedClient;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.ownsClient) this.client.start();
    await this.client.request({
      type: 'initEmbedder',
      embedder: {
        wasmBaseUrl: this.opts.wasmBaseUrl,
        modelAssetPath: this.opts.modelAssetPath,
      },
    });
    this.initialized = true;
  }

  isReady(): boolean {
    return this.initialized;
  }

  async extract(video: HTMLVideoElement, box: DetectionBox): Promise<DeepFeature | null> {
    if (!this.initialized) return null;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w <= 0 || h <= 0) return null;
    const sx = Math.max(0, box.x * w);
    const sy = Math.max(0, box.y * h);
    const sw = Math.max(1, Math.min(w - sx, box.w * w));
    const sh = Math.max(1, Math.min(h - sy, box.h * h));
    try {
      const bitmap = await createImageBitmap(video, sx, sy, sw, sh);
      const result = (await this.client.request({ type: 'embed', bitmap }, [bitmap])) as {
        embedding: ArrayBuffer | null;
      };
      if (!result.embedding) return null;
      return {
        kind: 'deep',
        embedding: new Float32Array(result.embedding),
        sampleCount: 1,
      };
    } catch (err) {
      console.error('[DeepReIdExtractorWorker] extract failed', err);
      return null;
    }
  }

  dispose(): void {
    if (this.ownsClient) this.client.terminate();
    this.initialized = false;
  }
}
